import api from "@/lib/axios";

import type { Lead, LeadSearch, LeadStatus, SearchStatusResponse } from "../types/leads";

export enum LeadsKeys {
  AllLeads = "leads-all",
  SearchHistory = "leads-search-history",
  SearchStatus = "leads-search-status",
  SearchLeads = "leads-search-leads",
}

// --- Suggestions ---

export const getSearchSuggestion = async (): Promise<{ query: string }> => {
  const response = await api.get("/leads/suggestion");
  return response.data;
};

// --- Searches ---

export const createSearch = async (query: string): Promise<LeadSearch> => {
  const response = await api.post("/leads/searches", { query });
  return response.data;
};

export const confirmSearch = async (
  searchId: string,
  matchLimit: number,
): Promise<LeadSearch> => {
  const response = await api.post(`/leads/searches/${searchId}/confirm`, {
    matchLimit,
  });
  return response.data;
};

export const cancelSearch = async (searchId: string): Promise<LeadSearch> => {
  const response = await api.post(`/leads/searches/${searchId}/cancel`);
  return response.data;
};

export const getSearchStatus = async (
  searchId: string,
): Promise<SearchStatusResponse> => {
  const response = await api.get(`/leads/searches/${searchId}/status`);
  return response.data;
};

export const getSearchLeads = async (searchId: string): Promise<Lead[]> => {
  const response = await api.get(`/leads/searches/${searchId}/leads`);
  return response.data;
};

export const getSearchHistory = async (
  limit = 20,
  offset = 0,
): Promise<{ searches: LeadSearch[]; total: number }> => {
  const response = await api.get("/leads/searches", {
    params: { limit, offset },
  });
  return response.data;
};

// --- Leads ---

export interface LeadsResponse {
  data: Lead[];
  total: number;
  skip: number;
  limit: number;
}

export const getAllLeads = async (
  search?: string,
  status?: string,
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  skip?: number,
  limit?: number,
): Promise<LeadsResponse> => {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (status) params.status = status;
  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;
  if (skip !== undefined) params.skip = skip.toString();
  if (limit !== undefined) params.limit = limit.toString();

  const response = await api.get("/leads", { params });

  if (response.data && "data" in response.data) {
    return {
      data: response.data.data as Lead[],
      total: response.data.total,
      skip: response.data.skip,
      limit: response.data.limit,
    };
  }

  // Fallback for unexpected response shape
  const items = Array.isArray(response.data) ? response.data : [];
  return {
    data: items,
    total: items.length,
    skip: skip || 0,
    limit: limit || items.length,
  };
};

export const updateLeadStatus = async (
  leadId: string,
  status: LeadStatus,
): Promise<Lead> => {
  const response = await api.patch(`/leads/${leadId}/status`, { status });
  return response.data;
};

export const deleteLead = async (leadId: string): Promise<Lead> => {
  return updateLeadStatus(leadId, "deleted");
};
